import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { KeyRound, Copy, CreditCard, BadgeCheck, Globe } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { parseCredentials } from "@/lib/qrpay";
import QrScanner from "@/components/QrScanner";

const SYMBOL = { EUR: "\u20ac", USD: "$" };

// Take: a SEPARATE credentials scan that RETRIEVES/inspects payment credentials without paying.
export default function QrTakeDialog({ open, onOpenChange, onUse }) {
  const [stage, setStage] = useState("enter"); // enter | retrieved
  const [parsed, setParsed] = useState(null);
  const [onNet, setOnNet] = useState(false);
  const [name, setName] = useState("");
  const [ccy, setCcy] = useState("EUR");

  const reset = () => { setStage("enter"); setParsed(null); setOnNet(false); setName(""); setCcy("EUR"); };
  useEffect(() => { if (open) reset(); }, [open]);

  const retrieve = async (text) => {
    const p = parseCredentials(text);
    if (!p || !p.recipient) { toast.error("Could not read those credentials"); return; }
    setParsed(p);
    let net = false;
    let nm = p.recipient_name || p.recipient;
    let cc = p.currency || "EUR";
    try {
      const res = await api.get(`/sepa/resolve/${encodeURIComponent(p.recipient)}`);
      net = true; nm = res.data.name; cc = res.data.currency;
    } catch (e) { net = false; }
    setOnNet(net); setName(nm); setCcy(cc);
    setStage("retrieved");
    toast.success(net ? `Retrieved ${nm}` : "Retrieved external recipient");
  };

  const copy = () => {
    navigator.clipboard?.writeText(parsed?.recipient || "");
    toast.success("Recipient copied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#FFE600]" /> Take credentials
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Read payment credentials to inspect the recipient. This only retrieves — it never pays.
          </DialogDescription>
        </DialogHeader>

        {stage === "enter" && (
          <div className="space-y-3 pt-1">
            <QrScanner onScan={(text) => retrieve(text)} onError={() => {}} />
            <p className="text-center text-xs text-[#52525B]">Point your camera at a SIGNET or payment QR to retrieve it</p>
          </div>
        )}

        {stage === "retrieved" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-1" data-testid="qrtake-retrieved">
            <div className="rounded-2xl border border-[#FFE600]/25 bg-[#FFE600]/[0.05] p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span data-testid="qrtake-recipient" className="font-mono-sig text-[#FFE600] break-all">{name}</span>
                <span className={`inline-flex items-center gap-1 font-mono-sig text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full ${onNet ? "bg-[#00FF9D]/15 text-[#00FF9D]" : "bg-[#FFB020]/15 text-[#FFB020]"}`}>
                  {onNet ? <><BadgeCheck className="w-3 h-3" /> On-net</> : <><Globe className="w-3 h-3" /> External</>}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0A0AB]">Recipient</span>
                <span data-testid="qrtake-recipient-id" className="font-mono-sig text-white break-all text-right max-w-[65%]">{parsed?.recipient}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0A0AB]">Scheme</span>
                <span className="font-mono-sig text-white uppercase">{parsed?.scheme}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0A0AB]">Amount</span>
                <span data-testid="qrtake-amount" className="font-mono-sig text-white">
                  {parsed?.amount ? `${SYMBOL[ccy] || ""}${Number(parsed.amount).toFixed(2)} ${ccy}` : "open"}
                </span>
              </div>
              {parsed?.reference && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#A0A0AB]">Reference</span>
                  <span className="font-mono-sig text-white">{parsed.reference}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="qrtake-copy" variant="outline" onClick={copy} className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10 h-11">
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
              <Button data-testid="qrtake-back" variant="outline" onClick={() => setStage("enter")} className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10 h-11">
                New
              </Button>
            </div>
            <Button
              data-testid="qrtake-pay-button"
              onClick={() => { onUse?.({ ...parsed, recipient_name: name, currency: ccy }); onOpenChange(false); }}
              className="w-full h-12 bg-[#00C2FF] text-black hover:bg-[#00C2FF]/90 font-semibold rounded-xl"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Pay this
            </Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
